/**
 * Color Generation Utilities for Schedule Grid
 *
 * Generates distinct, theme-aligned colors for class blocks based on
 * different categorization modes (styles, teachers, rooms, disciplines).
 */

/**
 * Color code mode options
 */
export type ColorCodeMode = 'styles' | 'teachers' | 'rooms' | 'disciplines';

/**
 * HSL color representation
 */
interface HSL {
  h: number; // Hue (0-360)
  s: number; // Saturation (0-100)
  l: number; // Lightness (0-100)
}

/**
 * Convert hex color to HSL
 */
function hexToHSL(hex: string): HSL {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  const hNorm = h / 360;
  const sNorm = s / 100;
  const lNorm = l / 100;

  let r: number;
  let g: number;
  let b: number;

  if (sNorm === 0) {
    r = lNorm;
    g = lNorm;
    b = lNorm;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      let tNorm = t;
      if (tNorm < 0) tNorm += 1;
      if (tNorm > 1) tNorm -= 1;
      if (tNorm < 1 / 6) return p + (q - p) * 6 * tNorm;
      if (tNorm < 1 / 2) return q;
      if (tNorm < 2 / 3) return p + (q - p) * (2 / 3 - tNorm) * 6;
      return p;
    };

    const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm;
    const p = 2 * lNorm - q;

    r = hue2rgb(p, q, hNorm + 1 / 3);
    g = hue2rgb(p, q, hNorm);
    b = hue2rgb(p, q, hNorm - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Generate N distinct colors from a base theme color
 * Colors are distributed evenly around the color wheel with variations in saturation/lightness
 *
 * @param baseColor - Base theme color in hex format (e.g., '#97293E')
 * @param count - Number of distinct colors to generate
 * @returns Array of hex color strings
 */
export function generateColors(baseColor: string, count: number): string[] {
  if (count === 0) return [];
  if (count === 1) return [baseColor];

  const baseHSL = hexToHSL(baseColor);
  const colors: string[] = [];

  // Golden ratio for better distribution
  const goldenRatio = 0.618033988749895;
  let hue = baseHSL.h;

  for (let i = 0; i < count; i += 1) {
    // Distribute hues using golden ratio for better perceptual distribution
    hue = (hue + goldenRatio * 360) % 360;

    // Vary saturation and lightness for additional distinction
    // Keep saturation between 45-75% for good color vibrancy
    const saturation = 45 + ((i * 7) % 30);

    // Keep lightness between 40-60% for good contrast with white text
    const lightness = 40 + ((i * 5) % 20);

    colors.push(hslToHex(Math.round(hue), saturation, lightness));
  }

  return colors;
}

/**
 * Create a color map for items based on the color code mode
 *
 * @param items - Array of item IDs or names
 * @param baseColor - Base theme color for generating variations
 * @returns Map of item ID/name to color hex string
 */
export function createColorMap(
  items: string[],
  baseColor: string
): Map<string, string> {
  const uniqueItems = Array.from(new Set(items));
  const colors = generateColors(baseColor, uniqueItems.length);
  const colorMap = new Map<string, string>();

  uniqueItems.forEach((item, index) => {
    colorMap.set(item, colors[index]);
  });

  return colorMap;
}

/**
 * Get color for a class block based on the selected color code mode
 *
 * @param classDetails - Class details from scheduled class
 * @param roomId - Room ID
 * @param teacherIds - Array of teacher IDs
 * @param mode - Color code mode
 * @param colorMap - Map of item to color
 * @param fallbackColor - Fallback color if no match found
 * @returns Hex color string
 */
export function getColorForClass(
  classDetails: {
    styles?: Array<{ id: string; disciplines?: string[] }>;
  },
  roomId: string,
  teacherIds: string[],
  mode: ColorCodeMode,
  colorMap: Map<string, string>,
  fallbackColor: string
): string {
  let key: string | undefined;

  switch (mode) {
    case 'styles':
      // Use first style ID
      key = classDetails.styles?.[0]?.id;
      break;
    case 'teachers':
      // Use first teacher ID
      [key] = teacherIds;
      break;
    case 'rooms':
      // Use room ID
      key = roomId;
      break;
    case 'disciplines':
      // Use first discipline from first style
      key = classDetails.styles?.[0]?.disciplines?.[0];
      break;
    default:
      key = undefined;
  }

  return colorMap.get(key || '') || fallbackColor;
}

/**
 * Storage key for color code mode preference
 */
const STORAGE_KEY = 'scheduleColorCodeMode';

/**
 * Save color code mode preference to localStorage
 */
export function saveColorModePreference(mode: ColorCodeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (error) {
    // Silently fail if localStorage is not available
    console.warn('Failed to save color mode preference:', error);
  }
}

/**
 * Load color code mode preference from localStorage
 */
export function loadColorModePreference(): ColorCodeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (
      saved === 'styles' ||
      saved === 'teachers' ||
      saved === 'rooms' ||
      saved === 'disciplines'
    ) {
      return saved;
    }
  } catch (error) {
    // Silently fail if localStorage is not available
    console.warn('Failed to load color mode preference:', error);
  }
  return 'styles'; // Default
}
